import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Button } from 'primeng/button';
import Manufacturer from '../../../../../types/manufacturer/Manufacturer';
import ManufacturerWriteDTO from '../../../../../types/manufacturer/ManufacturerWriteDTO';
import { IsMaintainerDirective } from '../../../directives/is-maintainer.directive';
import { FormatDatePipe } from '../../../pipes/format-date.pipe';
import { RoleNamePipe } from '../../../pipes/role-name.pipe';
import { ManufacturerService } from '../../../services/manufacturer.service';
import {
    UpdateManufacturerDialogComponent
} from '../../shared/manufacturers/update-manufacturer-dialog/update-manufacturer-dialog.component';
import { PageHeaderComponent } from '../../shared/page-header/page-header.component';

@Component({
    selector: 'app-manufacturer-detail',
    templateUrl: './manufacturer-detail.component.html',
    styleUrls: [ './manufacturer-detail.component.scss' ],
    imports: [
        Button,
        RouterLink,
        PageHeaderComponent,
        UpdateManufacturerDialogComponent,
        FormatDatePipe,
        RoleNamePipe,
        IsMaintainerDirective
    ]
})
export class ManufacturerDetailComponent implements OnInit {
    public manufacturer: Manufacturer | null = null;
    public editableManufacturer: ManufacturerWriteDTO = new ManufacturerWriteDTO({});
    public displayUpdateDialog = false;

    constructor(
        private route: ActivatedRoute,
        private manufacturerService: ManufacturerService,
        private messageService: MessageService,
        private confirmationService: ConfirmationService
    ) {
    }

    /**
     * Initializes the component.
     */
    public ngOnInit(): void {
        this.loadManufacturer();
    }

    /**
     * Opens the edit dialog.
     */
    public openEditDialog(): void {
        this.editableManufacturer = new ManufacturerWriteDTO(this.manufacturer ?? {});
        this.displayUpdateDialog = true;
    }

    /**
     * Reloads the manufacturer details.
     */
    public reloadManufacturerDetails(): void {
        this.loadManufacturer();
    }

    /**
     * Confirms the deletion of the manufacturer.
     */
    public confirmDelete(): void {
        if (!this.manufacturer) {
            return;
        }

        this.confirmationService.confirm({
            message: `Are you sure you want to delete the manufacturer '${ this.manufacturer.name }'?`,
            header: 'Confirm Deletion',
            icon: 'pi pi-exclamation-triangle',
            accept: () => {
                if (!this.manufacturer) {
                    return;
                }
                this.manufacturerService
                    .deleteManufacturer(this.manufacturer.id)
                    .then(() => {
                        this.messageService.add({
                            severity: 'success',
                            summary: 'Deleted',
                            detail: 'Manufacturer deleted successfully',
                            life: 3000,
                        });
                        // Navigate back to the manufacturers page
                        window.history.back();
                    })
                    .catch((error: any) => {
                        this.messageService.add({
                            severity: 'error',
                            summary: 'Error',
                            detail: error.message,
                            life: 3000,
                        });
                    });
            },
        });
    }

    /**
     * Loads the manufacturer details.
     */
    private loadManufacturer(): void {
        const id = this.route.snapshot.paramMap.get('id');
        if (id) {
            const idNumber = parseInt(id, 10);
            this.manufacturerService
                .getManufacturer(idNumber)
                .then((result: Manufacturer) => {
                    this.manufacturer = result;
                })
                .catch((error: any) => {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: error.message,
                        life: 3000,
                    });
                });
        }
    }
}
